import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Discussion from 'flarum/common/models/Discussion';
import Post from 'flarum/common/models/Post';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import { refreshAllGraphs, renderGraph } from '../common/d3GraphRenderer';

function debounce(fn: () => void, waitMs: number) {
  let timeout: number | null = null;

  return () => {
    if (timeout !== null) {
      window.clearTimeout(timeout);
    }
    timeout = window.setTimeout(() => {
      timeout = null;
      fn();
    }, waitMs);
  };
}

const scheduleGraphRefresh = debounce(() => {
  void refreshAllGraphs();
}, 250);

function triggerForumRefreshClick(): void {
  const el = document.querySelector(
    '.item-refresh button, .item-refresh a, .item-refresh [role="button"], .item-refresh'
  );

  if (el instanceof HTMLElement) {
    el.click();
  }
}

const scheduleForumRefresh = debounce(() => {
  triggerForumRefreshClick();
}, 50);

function isElement(target: unknown): target is Element {
  return !!target && typeof (target as any).closest === 'function';
}

function clickedRefreshItem(event: Event): boolean {
  const target = event.target;
  if (!isElement(target)) return false;
  return !!target.closest('.item-refresh');
}

function installRefreshClickHandlerOnce(): void {
  const anyApp = app as any;
  if (anyApp.__cipGraphRefreshClickHandlerInstalled) return;
  anyApp.__cipGraphRefreshClickHandlerInstalled = true;

  document.addEventListener(
    'click',
    (event) => {
      if (clickedRefreshItem(event)) {
        scheduleGraphRefresh();
      }
    },
    { capture: true }
  );
}

function shouldRefreshAfterRequest(options: any): boolean {
  if (!options || typeof options !== 'object') return false;

  const method = typeof options.method === 'string' ? options.method.toUpperCase() : '';
  const url = typeof options.url === 'string' ? options.url : '';

  // Only consider mutations, and only for posts/discussions resources.
  if (!['POST', 'PATCH', 'DELETE'].includes(method)) return false;
  if (!/(?:\/api)?\/(?:posts|discussions)\//.test(url)) return false;

  // Explicit hide/restore endpoints (typical Flarum routes).
  if (/(?:\/hide|\/restore)(?:\?|$)/.test(url)) return true;

  // Permanent delete of a post/discussion.
  if (method === 'DELETE') return true;

  // Fallback: some setups use PATCH to toggle hidden state.
  const data = options.data;
  const attributes = data?.data?.attributes ?? null;
  const hiddenAt = attributes?.hiddenAt;
  const isHidden = attributes?.isHidden;
  if (typeof hiddenAt !== 'undefined') return true;
  if (typeof isHidden !== 'undefined') return true;

  return false;
}

function installRequestRefreshHookOnce(): void {
  const anyApp = app as any;
  if (anyApp.__cipGraphRequestHookInstalled) return;
  anyApp.__cipGraphRequestHookInstalled = true;

  extend(app, 'request', function (promise: Promise<unknown>, options: any) {
    const shouldRefresh = shouldRefreshAfterRequest(options);

    return Promise.resolve(promise).then((result) => {
      if (shouldRefresh) {
        scheduleForumRefresh();
        scheduleGraphRefresh();
      }

      return result;
    });
  });
}

function installModelRefreshHooksOnce(): void {
  const anyApp = app as any;
  if (anyApp.__cipGraphModelHooksInstalled) return;
  anyApp.__cipGraphModelHooksInstalled = true;

  // Discussion hide/restore flows typically use `save()` with isHidden/hiddenAt.
  extend(Discussion.prototype as any, 'save', function (promise: Promise<unknown>, attributes: any) {
    const shouldRefresh =
      !!attributes &&
      typeof attributes === 'object' &&
      (Object.prototype.hasOwnProperty.call(attributes, 'isHidden') ||
        Object.prototype.hasOwnProperty.call(attributes, 'hiddenAt'));

    return Promise.resolve(promise).then((result) => {
      if (shouldRefresh) {
        scheduleForumRefresh();
        scheduleGraphRefresh();
      }
      return result;
    });
  });

  // Hard-delete discussions.
  extend(Discussion.prototype as any, 'delete', function (promise: Promise<unknown>) {
    return Promise.resolve(promise).then((result) => {
      scheduleForumRefresh();
      scheduleGraphRefresh();
      return result;
    });
  });

  // Post hide/restore flows typically use `save()` with isHidden/hiddenAt.
  extend(Post.prototype as any, 'save', function (promise: Promise<unknown>, attributes: any) {
    const shouldRefresh =
      !!attributes &&
      typeof attributes === 'object' &&
      (Object.prototype.hasOwnProperty.call(attributes, 'isHidden') ||
        Object.prototype.hasOwnProperty.call(attributes, 'hiddenAt'));

    return Promise.resolve(promise).then((result) => {
      if (shouldRefresh) {
        scheduleForumRefresh();
        scheduleGraphRefresh();
      }
      return result;
    });
  });

  // Hard-delete posts.
  extend(Post.prototype as any, 'delete', function (promise: Promise<unknown>) {
    return Promise.resolve(promise).then((result) => {
      scheduleForumRefresh();
      scheduleGraphRefresh();
      return result;
    });
  });
}

function currentRelativePath(): string {
  const baseUrl = app.forum.attribute('baseUrl') as string | undefined;
  let path = window.location.pathname;

  if (baseUrl) {
    try {
      const basePath = new URL(baseUrl).pathname.replace(/\/+$/, '');

      if (path.startsWith(basePath)) {
        path = path.slice(basePath.length) || '/';
      }
    } catch {
      // ignore
    }
  }

  return path || '/';
}

function isHomeDiscussionIndex(): boolean {
  return currentRelativePath() === '/';
}

function isTagDiscussionIndex(): boolean {
  return currentRelativePath().startsWith('/t/');
}

const GraphAccordionWidget = {
  oninit(this: any, vnode: any) {
    this.expanded = !!(vnode && vnode.attrs && vnode.attrs.initiallyExpanded);
    this.discussionId = vnode && vnode.attrs && vnode.attrs.discussionId;
    this.graphInitialized = false;
    this.graphContainer = null as HTMLElement | null;
  },

  maybeInitGraph(this: any) {
    if (this.graphInitialized || !this.expanded || !this.graphContainer) {
      return;
    }

    this.graphInitialized = true;
    void renderGraph(this.graphContainer, {
      discussionId: this.discussionId,
    });
  },

  view(this: any) {
    const expanded = this.expanded as boolean;
    const isPerDiscussionGraph = !!this.discussionId;

    const toggle = () => {
      this.expanded = !this.expanded;
      if (this.expanded) {
        setTimeout(() => {
          this.maybeInitGraph();
        }, 0);
      }
    };

    return m('div', { className: 'ZepGraphAccordion' }, [
      m(
        'button',
        {
          type: 'button',
          className: 'ZepGraphAccordion__toggle Button Button--link',
          onclick: toggle,
        },
        [
          m(
            'span',
            { className: 'ZepGraphAccordion__toggleLabel' },
            isPerDiscussionGraph
              ? expanded
                ? 'Hide discussion graph'
                : 'Show discussion graph'
              : expanded
                ? 'Hide discussions graph'
                : 'Show discussions graph'
          ),
          m(
            'span',
            { className: 'ZepGraphAccordion__toggleIcon' },
            expanded ? '▴' : '▾'
          ),
        ]
      ),
      m(
        'div',
        {
          className:
            'ZepGraphAccordion__body' + (expanded ? ' ZepGraphAccordion__body--open' : ''),
        },
        m('div', {
          className: 'ZepGraphContainer',
          oncreate: (vnode: any) => {
            this.graphContainer = vnode.dom as HTMLElement;
            this.maybeInitGraph();
          },
          onupdate: (vnode: any) => {
            this.graphContainer = vnode.dom as HTMLElement;
            this.maybeInitGraph();
          },
        })
      ),
    ]);
  },
};

app.initializers.add('cip/graph-integration', () => {
  installRefreshClickHandlerOnce();
  installRequestRefreshHookOnce();
  installModelRefreshHooksOnce();

  extend(IndexPage.prototype, 'contentItems', function (items: any) {
    const forumApp = app as any;
    const isSearchActive =
      !!(
        forumApp.search &&
        typeof forumApp.search.getValue === 'function' &&
        forumApp.search.getValue()
      );

    if (isHomeDiscussionIndex()) {
      items.add(
        'cip-graph-integration.graph-home',
        m(GraphAccordionWidget as any, { initiallyExpanded: !isSearchActive }),
        1000
      );
      return;
    }

    if (isTagDiscussionIndex()) {
      items.add('cip-graph-integration.graph-tag', m(GraphAccordionWidget), 1000);
    }
  });

  extend(DiscussionPage.prototype, 'view', function (this: any, vnode: any) {
    const discussion = (this as any).discussion;
    const discussionId =
      discussion && typeof discussion.id === 'function'
        ? Number(discussion.id())
        : null;

    // If we can't resolve the discussion ID, fall back to the original view.
    if (!discussionId || !vnode) {
      return;
    }

    const graphWidget = m(GraphAccordionWidget as any, {
      initiallyExpanded: true,
      discussionId,
    });

    // Recursively walk the vnode tree and inject the graph widget as a child of
    // the `.DiscussionPage-stream` container for better visual integration.
    const injectIntoStream = (node: any): boolean => {
      if (!node) return false;

      const attrs = node.attrs || {};
      const className = (attrs.className || attrs.class) as string | undefined;

      if (className && className.split(/\s+/).includes('DiscussionPage-stream')) {
        if (Array.isArray(node.children)) {
          node.children.unshift(graphWidget);
        } else if (node.children) {
          node.children = [graphWidget, node.children];
        } else {
          node.children = [graphWidget];
        }

        return true;
      }

      const childrenArray = Array.isArray(node.children)
        ? node.children
        : node.children
          ? [node.children]
          : [];

      for (const child of childrenArray) {
        if (injectIntoStream(child)) {
          return true;
        }
      }

      return false;
    };

    if (Array.isArray(vnode.children)) {
      for (const child of vnode.children) {
        if (injectIntoStream(child)) {
          break;
        }
      }
    } else if (vnode.children) {
      injectIntoStream(vnode.children);
    }
  });
});
